import React from 'react';
import { Layout, Button, Space, Typography, Tooltip, Dropdown, Flex } from 'antd';
import type { MenuProps } from 'antd';
import {
  GithubOutlined, LogoutOutlined, UserOutlined, MenuFoldOutlined, MenuUnfoldOutlined, DownOutlined,
} from '@ant-design/icons';
import WorkerStatusBanner from './WorkerStatusBanner';

const { Header } = Layout;
const { Text } = Typography;

interface AppHeaderProps {
  collapsed: boolean;
  onToggleSider: () => void;
  email?: string;
  role?: string;
  isAdmin: boolean;
  onLogout: () => void;
}

/**
 * Top bar: rail toggle + source link on the left, worker status and identity
 * on the right.
 *
 * Identity is a dropdown rather than an inline "icon + email + (role) +
 * Logout" run. On the 1280px target that run needed ~330px and, with the
 * worker banner beside it, overflowed: the email wrapped onto a second line
 * and collided with the role. Collapsing it to one truncating trigger keeps
 * the bar to a single 48px line at any email length, and gives the account
 * actions somewhere to grow.
 */
const AppHeader: React.FC<AppHeaderProps> = ({ collapsed, onToggleSider, email, role, isAdmin, onLogout }) => {
  const accountMenu: MenuProps = {
    items: [
      {
        key: 'identity',
        disabled: true,
        label: (
          <div style={{ padding: '2px 0', cursor: 'default' }}>
            <div><Text strong>{email}</Text></div>
            <Text type="secondary">Signed in as {role}</Text>
          </div>
        ),
      },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Log out', onClick: onLogout },
    ],
  };

  return (
    // nowrap + a shrinkable right group is what actually prevents the wrap;
    // the dropdown only reduces how often it would be needed.
    <Header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
      <Space size={4} style={{ flex: '0 0 auto' }}>
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="bottomLeft">
          <Button
            type="text"
            size="small"
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onToggleSider}
          />
        </Tooltip>
        <Tooltip title="Source code on GitHub" placement="bottomLeft">
          <Button
            type="text"
            size="small"
            aria-label="Source code on GitHub"
            icon={<GithubOutlined />}
            href="https://github.com/forproxyband/proxy-network-diag"
            target="_blank"
            rel="noopener noreferrer"
          />
        </Tooltip>
      </Space>

      <Flex align="center" justify="flex-end" gap={8} style={{ flex: '1 1 auto', minWidth: 0 }}>
        {isAdmin && <WorkerStatusBanner />}
        <Dropdown menu={accountMenu} trigger={['click']} placement="bottomRight">
          <Button type="text" size="small" style={{ maxWidth: 220, minWidth: 0 }}>
            <Space size={6} style={{ minWidth: 0 }}>
              <UserOutlined />
              {/* min-width:0 lets the flex item shrink so ellipsis can engage. */}
              <Text style={{ maxWidth: 150 }} ellipsis={{ tooltip: email }}>{email}</Text>
              <DownOutlined style={{ fontSize: 10 }} />
            </Space>
          </Button>
        </Dropdown>
      </Flex>
    </Header>
  );
};

export default AppHeader;
